import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface ReportType {
  key: string;
  name: string;
  cadence: string;
  outputs: string[];
  sourceRef: string | null;
  built: boolean;
  /** Publishing needs the summary (or commentary, or overall message) written. */
  requiresSummary: boolean;
  /** Spreadsheet or deck downloads rendered from the frozen snapshot. */
  exports: Array<'xlsx' | 'pptx'>;
}

export interface ReportRow {
  id: string;
  type: string;
  typeName: string;
  periodStart: string;
  periodEnd: string;
  label: string | null;
  version: number;
  status: 'DRAFT' | 'PUBLISHED';
  correctsId: string | null;
  correctionNote: string | null;
  publishedAt: string | null;
  publishedBy: string | null;
  documentId: string | null;
  createdAt: string;
  updatedAt: string;
  current: boolean;
}

export interface Narrative {
  summary?: string;
  asks?: string;
  highlights?: string[];
  phaseLines?: Record<string, string>;
  nextFocus?: string;
  slideNotes?: Record<string, string>;
}

export interface ReportDetail extends ReportRow {
  narrative: Narrative;
  snapshot: { period: { label: string; start: string; end: string } } & Record<string, unknown>;
  draftCorrectionId: string | null;
  emDashFields: string[];
}

export const reportsKey = (programId: string) => ['reports', programId] as const;

export function useReportTypes(programId: string) {
  return useQuery({
    queryKey: [...reportsKey(programId), 'types'],
    queryFn: () => api.get<ReportType[]>(`/api/programs/${programId}/report-types`),
    staleTime: 5 * 60_000,
  });
}

export function useReports(programId: string, type?: string) {
  return useQuery({
    queryKey: [...reportsKey(programId), 'list', type ?? ''],
    queryFn: () => api.get<ReportRow[]>(`/api/programs/${programId}/reports${type ? `?type=${type}` : ''}`),
  });
}

export function useReport(programId: string, id: string) {
  return useQuery({
    queryKey: [...reportsKey(programId), 'one', id],
    queryFn: () => api.get<ReportDetail>(`/api/programs/${programId}/reports/${id}`),
  });
}

/** The latest published, current weekly report (for the Executive tab). */
export function latestWeekly(rows: ReportRow[] | undefined) {
  return rows?.find((r) => r.type === 'WEEKLY_STATUS' && r.status === 'PUBLISHED' && r.current) ?? null;
}

// Calendar helpers. Dates are plain YYYY-MM-DD strings in the program's timezone.
const DAY = 86_400_000;
export const addDays = (iso: string, n: number) =>
  new Date(Date.parse(`${iso}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10);
export const mondayOf = (iso: string) => addDays(iso, -((new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7));

/** Today's date in a timezone. */
export function todayIn(timeZone: string) {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(),
  );
}

/** Mondays from the program start up to this week, newest first. */
export function weeksToReport(timeZone: string, programStart: string, limit = 26) {
  const out: string[] = [];
  const first = mondayOf(programStart);
  for (let m = mondayOf(todayIn(timeZone)); m >= first && out.length < limit; m = addDays(m, -7)) out.push(m);
  return out;
}

/** Months from the program start up to this month ("2026-10"), newest first. */
export function monthsToReport(timeZone: string, programStart: string, limit = 24) {
  const out: string[] = [];
  const first = programStart.slice(0, 7);
  let m = todayIn(timeZone).slice(0, 7);
  while (m >= first && out.length < limit) {
    out.push(m);
    const d = new Date(`${m}-01T00:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() - 1);
    m = d.toISOString().slice(0, 7);
  }
  return out;
}

export const EM_DASH = '\u2014';

const quarterOf = (iso: string) =>
  `${iso.slice(0, 4)}-${String(Math.floor((Number(iso.slice(5, 7)) - 1) / 3) * 3 + 1).padStart(2, '0')}-01`;
export const quarterLabel = (start: string) =>
  `Q${Math.floor((Number(start.slice(5, 7)) - 1) / 3) + 1} ${start.slice(0, 4)}`;

/** First days of quarters from the program start up to this quarter, newest first. */
export function quartersToReport(timeZone: string, programStart: string, limit = 12) {
  const out: string[] = [];
  const first = quarterOf(programStart);
  for (let q = quarterOf(todayIn(timeZone)); q >= first && out.length < limit;) {
    out.push(q);
    const d = new Date(`${q}T00:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() - 3);
    q = d.toISOString().slice(0, 10);
  }
  return out;
}

/** Download a file the API renders on request (spreadsheets, decks). The server audits it. */
export async function downloadFrom(url: string, fallbackName: string) {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(res.status === 404 ? 'That export is not available.' : 'The download failed.');
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName;
  const href = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement('a'), { href, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

/** Download a report's spreadsheet or deck. */
export const downloadExport = (programId: string, reportId: string, format: 'xlsx' | 'pptx') =>
  downloadFrom(`/api/programs/${programId}/reports/${reportId}/export/${format}`, `report.${format}`);
